/**
 * useOperationsData — composes the inputs the Operations tab needs into a
 * single tenant-scoped payload: heatmap matrix, ranked alerts, live-tail
 * lines, and last-sync timestamp.
 *
 * Reuses query keys from PillarCompliance / useComplianceDashboard so the
 * react-query cache is shared — opening the Operations tab after Posture
 * has loaded fires no extra requests.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { useComplianceDashboard } from '@/hooks/useComplianceData'
import {
  bpaSeverityLevel,
  coverageHealthLevel,
  driftSeverityLevel,
  getWorkloadForDriftComponent,
  heatRank,
  type HeatLevel,
  type SeverityBuckets,
} from '@/lib/workloadMapping'
import {
  type ActivityItem,
  type CollectionHealth,
  type ConfigurationDrift,
  type DashboardStats,
  type M365Workload,
  type WorkloadCollectionHealth,
  type WorkloadSummary,
  DriftSeverity,
} from '@/types'

// Same query key PillarCompliance uses, so cache hits.
const ACTIVE_DRIFT_QUERY_KEY_PREFIX = 'pillar-compliance-active-drifts'
const ACTIVE_DRIFT_STATUSES = 'New,Pending,PendingApproval,AwaitingRemediation'

const HEATMAP_WORKLOADS_CANONICAL: ReadonlyArray<M365Workload> = [
  'Identity', 'Exchange', 'Teams', 'SharePoint', 'Defender',
] as const

const CANONICAL_ORDER: Record<M365Workload, number> = {
  Identity:   0,
  Exchange:   1,
  Teams:      2,
  SharePoint: 3,
  Defender:   4,
  Other:      5,
}

const MAX_TAIL = 20

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface HeatmapCell {
  workload: M365Workload
  level: HeatLevel
}

export interface HeatmapRow {
  label: 'Drift' | 'Best Practice' | 'Coverage'
  /** null = no signal for that workload-row combination → render as a gap. */
  cells: Array<HeatmapCell | null>
}

export interface OperationsHeatmap {
  workloads: M365Workload[]
  rows: HeatmapRow[]
}

export interface OperationsTailLine {
  /** HH:mm:ss */
  stamp: string
  level: 'crit' | 'warn' | 'ok' | 'info'
  msg: string
}

export interface OperationsData {
  heatmap: OperationsHeatmap
  tail: OperationsTailLine[]
  lastUpdated: string | null
  isLoading: boolean
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useOperationsData(
  tenantId: string | undefined,
  stats: DashboardStats | null | undefined,
): OperationsData {
  const {
    dashboardData,
    collectionHealth,
    isLoading: complianceLoading,
  } = useComplianceDashboard(tenantId)

  const { data: activeDriftsData, isLoading: driftsLoading } = useQuery({
    queryKey: [ACTIVE_DRIFT_QUERY_KEY_PREFIX, tenantId],
    queryFn: () =>
      api.getConfigurationDrifts({
        tenantId,
        status: ACTIVE_DRIFT_STATUSES,
        page: 1,
        limit: 25,
      }),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  })

  const heatmap = useMemo<OperationsHeatmap>(
    () =>
      buildHeatmap({
        drifts:           activeDriftsData?.items ?? [],
        workloadSummary:  dashboardData?.workloadSummary ?? [],
        collectionHealth: collectionHealth ?? [],
      }),
    [activeDriftsData, dashboardData, collectionHealth],
  )

  const tail = useMemo<OperationsTailLine[]>(
    () => buildTail(stats?.recentActivity ?? []),
    [stats?.recentActivity],
  )

  return {
    heatmap,
    tail,
    lastUpdated: stats?.complianceScore?.lastUpdated ?? null,
    isLoading: complianceLoading || driftsLoading,
  }
}

// ---------------------------------------------------------------------------
// Heatmap builder
// ---------------------------------------------------------------------------

interface BuildHeatmapInput {
  drifts: ConfigurationDrift[]
  workloadSummary: WorkloadSummary[]
  collectionHealth: WorkloadCollectionHealth[]
}

function buildHeatmap(input: BuildHeatmapInput): OperationsHeatmap {
  // 1. Bucket active drifts by workload + severity.
  const driftByWorkload = new Map<M365Workload, SeverityBuckets>()
  for (const wl of [...HEATMAP_WORKLOADS_CANONICAL, 'Other' as M365Workload]) {
    driftByWorkload.set(wl, { critical: 0, high: 0, medium: 0, low: 0 })
  }
  for (const drift of input.drifts) {
    const wl = getWorkloadForDriftComponent(drift.component)
    const b = driftByWorkload.get(wl)
    if (!b) continue
    if      (drift.severity === DriftSeverity.Critical) b.critical++
    else if (drift.severity === DriftSeverity.High)     b.high++
    else if (drift.severity === DriftSeverity.Medium)   b.medium++
    else if (drift.severity === DriftSeverity.Low)      b.low++
  }

  // 2. Index BPA workload summary.
  const bpaByWorkload = new Map<M365Workload, WorkloadSummary>()
  for (const ws of input.workloadSummary) {
    bpaByWorkload.set(ws.workload, ws)
  }

  // 3. Index coverage health (backend keys vary; normalize).
  const coverageByWorkload = new Map<M365Workload, CollectionHealth>()
  for (const wch of input.collectionHealth) {
    const wl = normalizeCollectionHealthWorkload(wch.workload)
    if (wl) coverageByWorkload.set(wl, wch.health)
  }

  // 4. Build per-workload candidate rows.
  type Candidate = {
    workload: M365Workload
    drift:    HeatLevel
    bpa:      HeatLevel | null
    coverage: HeatLevel | null
    sumRank:  number
  }
  const candidates: Candidate[] = []

  const collect = (wl: M365Workload) => {
    const driftLvl = driftSeverityLevel(driftByWorkload.get(wl)!)
    const bpaLvl   = bpaSeverityLevel(bpaByWorkload.get(wl))
    const covHealth = coverageByWorkload.get(wl)
    const covLvl   = covHealth ? coverageHealthLevel(covHealth) : null

    const sumRank =
      heatRank(driftLvl) +
      (bpaLvl ? heatRank(bpaLvl) : 0) +
      (covLvl ? heatRank(covLvl) : 0)

    candidates.push({ workload: wl, drift: driftLvl, bpa: bpaLvl, coverage: covLvl, sumRank })
  }

  for (const wl of HEATMAP_WORKLOADS_CANONICAL) collect(wl)

  // 5. Append "Other" only if it has drift signal — never as a fake-healthy column.
  const otherDrift = driftByWorkload.get('Other')!
  const otherHasSignal =
    otherDrift.critical + otherDrift.high + otherDrift.medium + otherDrift.low > 0
  if (otherHasSignal) collect('Other')

  // 6. Drop columns where every row is l1 / null. Avoid the all-green cliché.
  const visible = candidates.filter(c => {
    const driftFlat = c.drift === 'l1'
    const bpaFlat   = c.bpa === null || c.bpa === 'l1'
    const covFlat   = c.coverage === null || c.coverage === 'l1'
    return !(driftFlat && bpaFlat && covFlat)
  })

  // 7. Sort by total severity desc, canonical order as tiebreaker.
  visible.sort((a, b) => {
    if (b.sumRank !== a.sumRank) return b.sumRank - a.sumRank
    return CANONICAL_ORDER[a.workload] - CANONICAL_ORDER[b.workload]
  })

  const workloads = visible.map(v => v.workload)
  const rows: HeatmapRow[] = [
    {
      label: 'Drift',
      cells: visible.map(v => ({ workload: v.workload, level: v.drift })),
    },
    {
      label: 'Best Practice',
      cells: visible.map(v =>
        v.bpa ? { workload: v.workload, level: v.bpa } : null,
      ),
    },
    {
      label: 'Coverage',
      cells: visible.map(v =>
        v.coverage ? { workload: v.workload, level: v.coverage } : null,
      ),
    },
  ]

  return { workloads, rows }
}

// Backend collectionHealth.workload may be a workload string, an M365DSC
// resource category, or a friendly name. Map to the canonical M365Workload.
function normalizeCollectionHealthWorkload(s: string): M365Workload | null {
  const u = (s ?? '').toUpperCase()
  if (!u) return null
  if (u.includes('AAD') || u.includes('IDENTITY') || u.includes('ENTRA')) return 'Identity'
  if (u.includes('EXO') || u.includes('EXCHANGE'))                         return 'Exchange'
  if (u.includes('TEAMS'))                                                 return 'Teams'
  if (u.includes('SPO') || u.includes('SHAREPOINT') || u.includes('ONEDRIVE') || u === 'OD') return 'SharePoint'
  if (u.includes('DEFENDER') || u.includes('INTUNE') || u.includes('MDO')) return 'Defender'
  return null
}

// ---------------------------------------------------------------------------
// Live tail builder
// ---------------------------------------------------------------------------

const FAILED_TYPES: ReadonlyArray<ActivityItem['type']> = [
  'compliance_scan_failed',
  'baseline_refresh_failed',
  'scuba_scan_failed',
  'bpa_scan_failed',
  'app_posture_scan_failed',
] as const

const PARTIAL_TYPES: ReadonlyArray<ActivityItem['type']> = [
  'compliance_scan_partial',
  'baseline_refresh_partial',
  'scuba_scan_partial',
  'bpa_scan_partial',
  'app_posture_scan_partial',
] as const

const OK_TYPES: ReadonlyArray<ActivityItem['type']> = [
  'drift_resolved',
  'drift_remediated',
  'drift_approved_by_user',
  'compliance_scan',
  'baseline_refresh',
  'scuba_scan',
  'bpa_scan',
  'app_posture_scan',
] as const

function buildTail(activity: ActivityItem[]): OperationsTailLine[] {
  return [...activity]
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, MAX_TAIL)
    .map(ev => ({
      stamp: formatTailStamp(ev.timestamp),
      level: levelForActivity(ev),
      msg:   ev.title,
    }))
}

function levelForActivity(ev: ActivityItem): 'crit' | 'warn' | 'ok' | 'info' {
  if (FAILED_TYPES.includes(ev.type))                                  return 'crit'
  if (ev.type === 'drift_detected' && ev.severity === DriftSeverity.Critical) return 'crit'
  if (PARTIAL_TYPES.includes(ev.type))                                 return 'warn'
  if (ev.type === 'drift_detected' && ev.severity === DriftSeverity.High)     return 'warn'
  if (OK_TYPES.includes(ev.type))                                      return 'ok'
  return 'info'
}

function formatTailStamp(iso: string | null | undefined): string {
  if (!iso) return '--:--:--'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '--:--:--'
  return d.toLocaleTimeString('en-GB', { hour12: false })
}
